import { useState, useCallback, Dispatch, SetStateAction } from "react";
import moment from "moment";
import {
  mapNeurapulseFilterTypesReverse,
  INeurapulseFilter,
} from "src/components/common/NeurapulseFilter";
import { ISwitchFilter } from "src/components/common/SwitchFilter";
import {
  NeurapulseTestCompletionStatusType,
} from "src/common/Enums";
import { IDate } from "src/common/IDate";

// All display-filter names as a union for type safety
type DisplayFilterKey =
  | "displayVitalsFilters"
  | "displayAppraisalFilters"
  | "displayNeurapulseFilters"
  | "displayCerrorFilters"
  | "displayTerrorFilters"
  | "displayOnroadRecommendationFilters"
  | "displayInofficeRecommendationFilters";

export interface ResultsFilterState {
  filtered: any[];
  vitalsStatus: ISwitchFilter;
  cErrorStatus: ISwitchFilter;
  tErrorStatus: ISwitchFilter;
  appraisalStatus: ISwitchFilter;
  neurapulseStatus: INeurapulseFilter;
  onroadRecommendationStatus: ISwitchFilter;
  inofficeRecommendationStatus: ISwitchFilter;
  vitalsFilter: any[];
  cErrorFilter: CoreScoringDecisionType[];
  tErrorFilter: CoreScoringDecisionType[];
  appraisalFilter: CoreScoringDecisionType[];
  neurapulseFilter: NeurapulseTestCompletionStatusType[];
  onroadRecommendationFilter: OnroadRecommendationType[];
  inofficeRecommendationFilter: CareScoringDecisionType[];
  displayFilters: Record<DisplayFilterKey, boolean>;
  dateCreated: IDate | null;
  neurapulseCompleted: IDate | null;
  coreDateCompleted: IDate | null;
  vitalsDateCompleted: IDate | null;
  careDateCompleted: IDate | null;
  showFilters: boolean;
}

// Converts camelCase filter keys to filter value strings for react-table
function newFilterValue(filterValues: string[]): any[] {
  if (filterValues.length < 1) return [];
  return filterValues.map((val) => {
    if (val === "fail") return "Fail";
    if (val === "borderline") return "Borderline";
    if (val === "indeterminate") return "Indeterminate";
    if (val === "pass") return "Pass";
    if (val === "lowRisk") return "LowRisk";
    if (val === "rangeOfInquiry") return "RangeOfInquiry";
    if (val === "highRisk") return "HighRisk";
    if (val === "hazardous") return "Hazardous";
    if (val === "completed") return "C";
    if (val === "notCompleted") return "NC";
    if (val === "aborted") return "Aborted";
    return null;
  }).filter((v) => v !== null);
}

function getActiveFilterValues(status: ISwitchFilter): any[] {
  const activeKeys = Object.keys(status).filter((k) => (status as any)[k] !== false);
  return newFilterValue(activeKeys);
}

const DEFAULT_DISPLAY_FILTERS: Record<DisplayFilterKey, boolean> = {
  displayVitalsFilters: false,
  displayAppraisalFilters: false,
  displayNeurapulseFilters: false,
  displayCerrorFilters: false,
  displayTerrorFilters: false,
  displayOnroadRecommendationFilters: false,
  displayInofficeRecommendationFilters: false,
};

export function useResultsFilters(canSeeScores: boolean) {
  const defaultVitalsStatus = canSeeScores
    ? ({ pass: false, fail: false, rangeOfInquiry: false } as ISwitchFilter)
    : ({ completed: false, notCompleted: false } as ISwitchFilter);

  const [filtered, setFiltered] = useState<any[]>([]);
  const [vitalsStatus, setVitalsStatus] = useState<ISwitchFilter>(defaultVitalsStatus);
  const [cErrorStatus, setCErrorStatus] = useState<ISwitchFilter>({
    pass: false, fail: false, hazardous: false, rangeOfInquiry: false,
  } as ISwitchFilter);
  const [tErrorStatus, setTErrorStatus] = useState<ISwitchFilter>({
    pass: false, fail: false, rangeOfInquiry: false,
  } as ISwitchFilter);
  const [appraisalStatus, setAppraisalStatus] = useState<ISwitchFilter>({
    pass: false, fail: false, rangeOfInquiry: false,
  } as ISwitchFilter);
  const [neurapulseStatus, setNeurapulseStatus] = useState<INeurapulseFilter>({
    completed: false, highRisk: false, incomplete: false,
  } as INeurapulseFilter);
  const [onroadRecommendationStatus, setOnroadRecommendationStatus] = useState<ISwitchFilter>({
    lowRisk: false, rangeOfInquiry: false, highRisk: false,
  } as ISwitchFilter);
  const [inofficeRecommendationStatus, setInofficeRecommendationStatus] = useState<ISwitchFilter>({
    pass: false, fail: false, rangeOfInquiry: false, aborted: false,
  } as ISwitchFilter);

  const [vitalsFilter, setVitalsFilter] = useState<any[]>([]);
  const [cErrorFilter, setCErrorFilter] = useState<CoreScoringDecisionType[]>([]);
  const [tErrorFilter, setTErrorFilter] = useState<CoreScoringDecisionType[]>([]);
  const [appraisalFilter, setAppraisalFilter] = useState<CoreScoringDecisionType[]>([]);
  const [neurapulseFilter, setNeurapulseFilter] = useState<NeurapulseTestCompletionStatusType[]>([]);
  const [onroadRecommendationFilter, setOnroadRecommendationFilter] = useState<OnroadRecommendationType[]>([]);
  const [inofficeRecommendationFilter, setInofficeRecommendationFilter] = useState<CareScoringDecisionType[]>([]);

  const [displayFilters, setDisplayFilters] = useState<Record<DisplayFilterKey, boolean>>(
    DEFAULT_DISPLAY_FILTERS
  );

  const [dateCreated, setDateCreated] = useState<IDate | null>(null);
  const [neurapulseCompleted, setNeurapulseCompleted] = useState<IDate | null>(null);
  const [coreDateCompleted, setCoreDateCompleted] = useState<IDate | null>(null);
  const [vitalsDateCompleted, setVitalsDateCompleted] = useState<IDate | null>(null);
  const [careDateCompleted, setCareDateCompleted] = useState<IDate | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(canSeeScores);

  // Sets a single switch-filter value and calls the column's onChange with updated filter array.
  // Computes post-toggle values synchronously to avoid async state read issues.
  const setFilterValue = useCallback(
    (switchProp: string, columnName: string, onChange: any) => {
      const applyToggle = (
        current: ISwitchFilter,
        setter: Dispatch<SetStateAction<ISwitchFilter>>,
        filterSetter: Dispatch<SetStateAction<any[]>>
      ) => {
        const updated = { ...current, [switchProp]: !(current as any)[switchProp] };
        const vals = getActiveFilterValues(updated);
        setter(updated);
        filterSetter(vals as any);
        onChange(vals);
      };

      switch (columnName) {
        case "vitalsStatus":
          applyToggle(vitalsStatus, setVitalsStatus, setVitalsFilter);
          break;
        case "cErrorStatus":
          applyToggle(cErrorStatus, setCErrorStatus, setCErrorFilter);
          break;
        case "tErrorStatus":
          applyToggle(tErrorStatus, setTErrorStatus, setTErrorFilter);
          break;
        case "appraisalStatus":
          applyToggle(appraisalStatus, setAppraisalStatus, setAppraisalFilter);
          break;
        case "onroadRecommendationStatus":
          applyToggle(
            onroadRecommendationStatus,
            setOnroadRecommendationStatus,
            setOnroadRecommendationFilter
          );
          break;
        case "inofficeRecommendationStatus":
          applyToggle(
            inofficeRecommendationStatus,
            setInofficeRecommendationStatus,
            setInofficeRecommendationFilter
          );
          break;
      }
    },
    [
      vitalsStatus, cErrorStatus, tErrorStatus, appraisalStatus,
      onroadRecommendationStatus, inofficeRecommendationStatus,
    ]
  );

  const setNeurapulseFilterValue = useCallback(
    (switchProp: string, _columnName: string, onChange: any) => {
      const updated = {
        ...neurapulseStatus,
        [switchProp]: !(neurapulseStatus as any)[switchProp],
      };
      const activeKeys = Object.keys(updated).filter((k) => (updated as any)[k] !== false);
      const activeFilterValues = activeKeys.map((val) => mapNeurapulseFilterTypesReverse(val)).flat();
      setNeurapulseStatus(updated);
      setNeurapulseFilter(activeFilterValues as NeurapulseTestCompletionStatusType[]);
      onChange(activeFilterValues);
    },
    [neurapulseStatus]
  );

  // Filter method passed to react-table switch-filter columns
  const setFilterMethodForSwitch = useCallback(
    (filter: any, row: ClientSiteAssessmentResultsDTO) => {
      const rowProperty = filter.id;
      const filterValue = filter.value;
      if (filterValue.length < 1) {
        setFiltered([]);
        return false;
      }
      const dynamicKey = rowProperty as keyof ClientSiteAssessmentResultsDTO;
      if (row[dynamicKey] !== undefined) {
        return filterValue.includes(row[dynamicKey]);
      }
      return handleCompleteNotCompleteFilter(row, rowProperty, filterValue);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setFilterMethodForNeurapulseSwitch = useCallback(
    (filter: any, row: any) => {
      const rowProperty = filter.id;
      const filterValue = filter.value;
      if (filterValue.length < 1) {
        setFiltered([]);
        return false;
      }
      if (row[rowProperty] !== undefined) {
        return filterValue.includes(row[rowProperty]);
      }
      return handleNeurapulseFilter(row, rowProperty, filterValue);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleCompleteNotCompleteFilter = useCallback(
    (row: ClientSiteAssessmentResultsDTO, rowProperty: string, filterValue: any): boolean => {
      let showC = false;
      let showNC = false;
      if (rowProperty === "vitalsStatusId") {
        if (filterValue.includes("C")) {
          showC = !row.vitalsStatusId && row.vitalsDateCompleted ? true : false;
        }
        if (filterValue.includes("NC")) {
          showNC = !row.vitalsStatusId && !row.vitalsDateCompleted ? true : false;
        }
      }
      return showC || showNC;
    },
    []
  );

  const handleNeurapulseFilter = useCallback(
    (row: any, rowProperty: string, filterValue: any): boolean => {
      let showCompleted = false;
      let showHighRisk = false;
      let showIncomplete = false;
      if (rowProperty === "neurapulseStatus") {
        if (filterValue.includes("completed")) {
          showCompleted =
            row.neurapulseStatus === NeurapulseTestCompletionStatusType.Completed;
        }
        if (filterValue.includes("highRisk")) {
          showHighRisk =
            row.neurapulseStatus === NeurapulseTestCompletionStatusType.HighRisk;
        }
        if (filterValue.includes("incomplete")) {
          showIncomplete =
            row.neurapulseStatus === NeurapulseTestCompletionStatusType.UnknownError ||
            row.neurapulseStatus === NeurapulseTestCompletionStatusType.UnknownStatus ||
            row.neurapulseStatus ===
              NeurapulseTestCompletionStatusType.CouldNotCompleteConnectionIssues ||
            row.neurapulseStatus ===
              NeurapulseTestCompletionStatusType.CouldNotCompleteNpServerIssues ||
            row.neurapulseStatus ===
              NeurapulseTestCompletionStatusType.CouldNotCompleteTimedOut ||
            row.neurapulseStatus ===
              NeurapulseTestCompletionStatusType.CouldNotCompleteUnsupportedDevice;
        }
      }
      return showCompleted || showHighRisk || showIncomplete;
    },
    []
  );

  const showOrHideDisplayFilter = useCallback((name: string) => {
    setDisplayFilters((prev) => {
      const currentValue = prev[name as DisplayFilterKey];
      return {
        ...DEFAULT_DISPLAY_FILTERS,
        [name]: !currentValue,
      };
    });
  }, []);

  const closeFilter = useCallback((displayFilterName: string) => {
    setDisplayFilters((prev) => ({
      ...prev,
      [displayFilterName]: !prev[displayFilterName as DisplayFilterKey],
    }));
  }, []);

  const clearFilters = useCallback(() => {
    const defaultStatus = canSeeScores
      ? ({ pass: false, fail: false, rangeOfInquiry: false } as ISwitchFilter)
      : ({ completed: false, notCompleted: false } as ISwitchFilter);

    setVitalsStatus(defaultStatus);
    setCErrorStatus({ pass: false, fail: false, hazardous: false, rangeOfInquiry: false } as ISwitchFilter);
    setTErrorStatus({ pass: false, fail: false, rangeOfInquiry: false } as ISwitchFilter);
    setAppraisalStatus({ pass: false, fail: false, rangeOfInquiry: false } as ISwitchFilter);
    setOnroadRecommendationStatus({ lowRisk: false, rangeOfInquiry: false, highRisk: false } as ISwitchFilter);
    setInofficeRecommendationStatus({ pass: false, fail: false, rangeOfInquiry: false, aborted: false } as ISwitchFilter);
    setNeurapulseStatus({ completed: false, highRisk: false, incomplete: false } as INeurapulseFilter);
    setVitalsFilter([]);
    setCErrorFilter([]);
    setTErrorFilter([]);
    setAppraisalFilter([]);
    setNeurapulseFilter([]);
    setOnroadRecommendationFilter([]);
    setInofficeRecommendationFilter([]);
    setDateCreated(null);
    setNeurapulseCompleted(null);
    setCoreDateCompleted(null);
    setVitalsDateCompleted(null);
    setCareDateCompleted(null);
    setDisplayFilters(DEFAULT_DISPLAY_FILTERS);
    setFiltered([]);
  }, [canSeeScores]);

  const onDateChange = useCallback(
    (accessor: string, onChange: any) => (e: any) => {
      const dateValue = {
        start: moment.utc(e[0]).local(),
        end: moment.utc(e[1]).local(),
      };
      switch (accessor) {
        case "dateCreated": setDateCreated(dateValue as IDate); break;
        case "neurapulseCompleted": setNeurapulseCompleted(dateValue as IDate); break;
        case "coreDateCompleted": setCoreDateCompleted(dateValue as IDate); break;
        case "vitalsDateCompleted": setVitalsDateCompleted(dateValue as IDate); break;
        case "careDateCompleted": setCareDateCompleted(dateValue as IDate); break;
      }
      onChange(e);
    },
    []
  );

  const filterState: ResultsFilterState = {
    filtered,
    vitalsStatus,
    cErrorStatus,
    tErrorStatus,
    appraisalStatus,
    neurapulseStatus,
    onroadRecommendationStatus,
    inofficeRecommendationStatus,
    vitalsFilter,
    cErrorFilter,
    tErrorFilter,
    appraisalFilter,
    neurapulseFilter,
    onroadRecommendationFilter,
    inofficeRecommendationFilter,
    displayFilters,
    dateCreated,
    neurapulseCompleted,
    coreDateCompleted,
    vitalsDateCompleted,
    careDateCompleted,
    showFilters,
  };

  return {
    filterState,
    filtered,
    setFiltered,
    setFilterValue,
    setNeurapulseFilterValue,
    setFilterMethodForSwitch,
    setFilterMethodForNeurapulseSwitch,
    handleCompleteNotCompleteFilter,
    handleNeurapulseFilter,
    showOrHideDisplayFilter,
    closeFilter,
    clearFilters,
    onDateChange,
    setShowFilters,
  };
}
